import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OWL_DATE_TIME_FORMATS } from '@danielmoncada/angular-datetime-picker';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowClickedEvent,
  ValueFormatterParams
} from 'ag-grid-community';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ColumnsDialogComponent } from 'src/app/user/jsignal-product/product-safety-strategy/columns-dialog/columns-dialog.component';
import { LocalStorageSettingsService } from 'src/app/shared/services/local-storage-settings.service';
import { JsignalService } from 'src/app/shared/services/jsignal.service';
import { ExceljsServiceService } from 'src/app/shared/services/exceljs-service.service';
import { AuditTrailServiceService } from '../audit-trail/audit-trail-service/audit-trail-service.service';

export const AUDIT_TRAIL_DATE_TIME_FORMATS = {
  parseInput: 'DD-MMM-YYYY',
  fullPickerInput: 'DD-MMM-YYYY',
  datePickerInput: 'DD-MMM-YYYY',
  timePickerInput: 'HH:mm',
  monthYearLabel: 'MMM YYYY',
  dateA11yLabel: 'LL',
  monthYearA11yLabel: 'MMMM YYYY'
};

/** Raw record shape returned by reteriveAuditInRange */
export interface AuditTrailApiRecord {
  id: number;
  createdBy: string;
  createdAt: string;
  modified_fields: string | null;
  affectedClassName: string;
  affectedRecordId: string | number;
  actionType: string;
  tableName: string | null;
  screenName: string | null;
  databaseColumn: string | null;
  screenlabel: string | null;
  referenceScreen: string | null;
  moduleName: string | null;
}

export interface AuditFieldChange {
  fieldName: string;
  screenLabel: string;
  previousValue: string;
  newValue: string;
}

export interface AuditEvent {
  auditId: number;
  timestamp: string;
  timestampRaw: string;
  user: string;
  action: string;
  affectedClassName: string;
  recordId: string;
  screenName: string;
  referenceScreen: string;
  moduleName: string;
  /** screenName if present, otherwise referenceScreen */
  displayScreen: string;
  tableName: string;
  databaseColumn: string;
  screenLabel: string;
  recordName: string;
  modifiedFieldsRaw: string;
  changedFields: number;
  fieldChanges: AuditFieldChange[];
}

export interface AuditFilterState {
  fromDate: Date | null;
  toDate: Date | null;
  user: string;
  affectedClassName: string;
  action: string;
  screenName: string;
  moduleName: string;
  tableName: string;
  recordName: string;
  recordId: string;
}

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-audit-trail-page',
  templateUrl: './audit-trail-page.component.html',
  styleUrls: ['./audit-trail-page.component.scss'],
  standalone: false,
  providers: [
    DatePipe,
    { provide: OWL_DATE_TIME_FORMATS, useValue: AUDIT_TRAIL_DATE_TIME_FORMATS }
  ]
})
export class AuditTrailPageComponent implements OnInit, OnDestroy {
  readonly filterStorageKey = 'audit-trail-page-saved-filter';
  readonly languageStorageKey = 'audit-trail-page-language';
  /** Named loader task — not killed by global spinner.stop() from interceptor / other APIs. */
  private readonly auditLoadTaskId = 'audit-trail-retrieve';
  private auditLoadSubscription: Subscription | null = null;

  /** Add more languages here as needed; code is sent to the API on Search. */
  readonly languageOptions: Array<{ label: string; code: string }> = [
    { label: 'English', code: 'en' },
    { label: 'Japanese', code: 'ja' }
  ];

  selectedLanguage = 'en';

  /** Hidden in grid by default; user can enable via Columns. */
  private readonly defaultHiddenFields = ['affectedClassName', 'recordId', 'recordName'];

  backgroundColorScreen = '';
  buttonColor = '';

  detailsExpanded = true;

  filters: AuditFilterState = this.createDefaultFilters();

  userOptions: Array<{ userName: string }> = [];
  filteredUserOptions: Array<{ userName: string }> = [];
  classOptions: SelectOption[] = [{ label: 'All Classes', value: '' }];
  /** Fixed uppercase action values — avoids Create/CREATE duplicates. */
  actionOptions: SelectOption[] = [
    { label: 'All Actions', value: '' },
    { label: 'CREATE', value: 'CREATE' },
    { label: 'UPDATE', value: 'UPDATE' },
    { label: 'DELETE', value: 'DELETE' }
  ];
  screenOptions: SelectOption[] = [{ label: 'All Screens', value: '' }];
  moduleOptions: SelectOption[] = [{ label: 'All Modules', value: '' }];
  tableOptions: SelectOption[] = [{ label: 'All Tables', value: '' }];

  pageSizeOptions = [10, 25, 50, 100];
  pageSize = 10;
  currentPage = 1;
  totalPages = 1;
  totalRows = 0;
  startRow = 0;
  endRow = 0;

  private allEvents: AuditEvent[] = [];
  filteredEvents: AuditEvent[] = [];
  selectedEvent: AuditEvent | null = null;

  columnDefs: ColDef[] = [];
  /** Shared with other screens (e.g. HA Request): sortable + column filter + resize. */
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    wrapText: true,
    autoHeight: true,
    minWidth: 100,
    maxWidth: 360,
    filterParams: {
      maxNumConditions: 2,
      numAlwaysVisibleConditions: 2,
      defaultJoinOperator: 'OR'
    }
  };

  gridOptions: GridOptions = {
    rowSelection: 'single',
    suppressRowClickSelection: false,
    animateRows: true,
    suppressCellFocus: true,
    headerHeight: 35,
    enableBrowserTooltips: true,
    suppressColumnVirtualisation: true,
    onGridSizeChanged: () => this.refreshGridColumnLayout(false)
  };

  private gridApi!: GridApi;
  private columnLayoutTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshingColumns = false;

  toggleableCols: Array<{ headerName: string; field: string }> = [];
  selectedFields: string[] = [];

  // Wires Angular DI services used by filters, grid, exports, and audit API calls.
  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private localStorageSettings: LocalStorageSettingsService,
    private auditTrailService: AuditTrailServiceService,
    private jsignalService: JsignalService,
    private spinner: NgxUiLoaderService,
    private datePipe: DatePipe,
    private exceljsService: ExceljsServiceService
  ) {}

  // Loads theme colors, grid columns, workspace users, and initial audit data for today.
  ngOnInit(): void {
    this.backgroundColorScreen = this.localStorageSettings.getSettings('ColorD');
    this.buttonColor = this.localStorageSettings.getSettings('ColorA');
    // Language / saved filters are session-only (localStorage persistence disabled).
    // this.restoreSelectedLanguage();
    this.initColumnDefs();
    // this.restoreSavedFilter();
    this.loadWorkspaceUsers();
    this.loadAuditEvents(false);
  }

  // Clears timers/subscriptions and stops the named audit spinner task on leave.
  ngOnDestroy(): void {
    if (this.columnLayoutTimer) {
      clearTimeout(this.columnLayoutTimer);
      this.columnLayoutTimer = null;
    }
    this.auditLoadSubscription?.unsubscribe();
    this.auditLoadSubscription = null;
    this.spinner.stop(this.auditLoadTaskId);
  }

  // Fetches workspace users to populate the searchable User filter dropdown.
  private loadWorkspaceUsers(): void {
    const workspaceId = this.localStorageSettings.getSettings('WorkspaceId');
    this.auditTrailService.retrieveAllUsersByWorkspace(workspaceId).subscribe({
      next: (response) => {
        const names = Array.isArray(response)
          ? response
              .map((user) => (typeof user === 'string' ? user.trim() : ''))
              .filter((user) => !!user)
          : [];
        this.userOptions = names.map((userName) => ({ userName }));
        this.filteredUserOptions = [...this.userOptions];
      },
      error: () => {
        this.userOptions = [];
        this.filteredUserOptions = [];
      }
    });
  }

  // Resets the filtered user list when the User dropdown opens.
  onUserDropdownOpened(opened: boolean): void {
    if (opened) {
      this.filteredUserOptions = [...this.userOptions];
    }
  }



  // Returns how many audit events are currently in the filtered grid dataset.
  get eventCount(): number {
    return this.filteredEvents.length;
  }

  // Returns field-change count for the currently selected event details panel.
  get fieldChangeCount(): number {
    return this.selectedEvent?.fieldChanges?.length ?? 0;
  }

  // Builds the Showing X to Y of Z entries label for the pager UI.
  get showingLabel(): string {
    if (!this.totalRows) {
      return 'Showing 0 to 0 of 0 entries';
    }
    return `Showing ${this.startRow} to ${this.endRow} of ${this.totalRows} entries`;
  }

  // Stores grid API, applies column visibility, and selects the first event when ready.
  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.gridApi.setGridOption('rowData', this.filteredEvents);
    this.gridApi.setGridOption('paginationPageSize', this.pageSize || 10);
    this.applyColumnVisibility();
    this.updatePaginationState();
    if (this.filteredEvents.length) {
      this.selectEvent(this.filteredEvents[0], true);
    }
  }

  // Opens Event Details for the clicked audit row without forcing grid re-selection.
  onRowClicked(event: RowClickedEvent): void {
    if (event?.data) {
      this.selectEvent(event.data as AuditEvent, false);
    }
  }

  // Expands or collapses the Event Details section below the grid.
  toggleDetails(): void {
    this.detailsExpanded = !this.detailsExpanded;
  }

  // Validates dates then reloads audit events from API using current filters/language.
  onSearch(): void {
    if (!this.filters.fromDate || !this.filters.toDate) {
      this.showValidationMessage('From Date and To Date are required.');
      return;
    }

    if (this.isToDateBeforeFromDate()) {
      this.showValidationMessage('To Date must not be older than From Date.');
      return;
    }

    this.loadAuditEvents(true);
  }

  // Restores default today filters and English, then reloads audit events.
  onReset(): void {
    this.filters = this.createDefaultFilters();
    this.selectedLanguage = 'en';
    this.loadAuditEvents(true);
    this.snackBar.open('Filters reset.', 'Close', { duration: 5000 });
  }

  // No-op on change; language is sent to API only when Search is clicked.
  onLanguageChange(): void {
    // no-op — kept for compatibility if bound in template later
  }

  // Shows session-only notice because localStorage filter persistence is disabled.
  onSaveFilter(): void {
    // Persistence disabled — preferences are not written to localStorage.
    // localStorage.setItem(this.filterStorageKey, JSON.stringify({
    //   user: this.filters.user || '',
    //   action: this.filters.action || '',
    //   affectedClassName: this.filters.affectedClassName || '',
    //   screenName: this.filters.screenName || '',
    //   moduleName: this.filters.moduleName || '',
    //   tableName: this.filters.tableName || '',
    //   recordName: this.filters.recordName || '',
    //   recordId: this.filters.recordId || ''
    // }));
    this.snackBar.open(
      'Save Filter storage is disabled. Filters apply for this session only.',
      'Close',
      { duration: 5000 }
    );
  }

  // Shows a bottom-centered snackbar so validation stays visible over overlays.
  private showValidationMessage(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['audit-trail-snackbar']
    });
  }

  // Builds and downloads the multi-sheet Excel audit report for filtered events.
  async onExportExcel(): Promise<void> {
    if (!this.filteredEvents.length) {
      this.snackBar.open('No records available to export.', 'Close', { duration: 5000 });
      return;
    }

    try {
      this.spinner.start('audit-trail-excel-export');
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'JSuite Audit Trail';
      workbook.created = new Date();

      this.buildExcelWorkbook(workbook);

      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Audit_Trail_Report_${this.getExportFileStamp()}.xlsx`;
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        fileName
      );
      this.snackBar.open('Excel report exported successfully.', 'Close', { duration: 5000 });
    } catch {
      this.snackBar.open('Failed to export Excel report.', 'Close', { duration: 5000 });
    } finally {
      this.spinner.stop('audit-trail-excel-export');
    }
  }

  // Opens a printable HTML report window so the user can Save as PDF.
  onExportPdf(): void {
    if (!this.filteredEvents.length) {
      this.snackBar.open('No records available to export.', 'Close', { duration: 5000 });
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1400,height=900');
    if (!printWindow) {
      this.snackBar.open('Unable to open print window. Please allow pop-ups.', 'Close', {
        duration: 5000
      });
      return;
    }

    printWindow.document.open();
    printWindow.document.write(this.buildCombinedPdfHtmlReport());
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 350);

    this.snackBar.open('PDF preview opened. Use Print > Save as PDF.', 'Close', {
      duration: 5000
    });
  }

  // Flattens each field change into one export row with repeated event context.
  private buildExportRows(): Array<{
    rowNo: number;
    auditId: number;
    timestamp: string;
    user: string;
    action: string;
    moduleName: string;
    screen: string;
    referenceScreen: string;
    className: string;
    tableName: string;
    recordId: string;
    recordName: string;
    fieldName: string;
    screenLabel: string;
    previousValue: string;
    newValue: string;
  }> {
    const rows: Array<{
      rowNo: number;
      auditId: number;
      timestamp: string;
      user: string;
      action: string;
      moduleName: string;
      screen: string;
      referenceScreen: string;
      className: string;
      tableName: string;
      recordId: string;
      recordName: string;
      fieldName: string;
      screenLabel: string;
      previousValue: string;
      newValue: string;
    }> = [];

    let rowNo = 0;
    this.filteredEvents.forEach((event) => {
      const base = {
        auditId: event.auditId,
        timestamp: event.timestamp,
        user: event.user,
        action: this.formatActionLabel(event.action),
        moduleName: event.moduleName || '-',
        screen: event.screenName || '-',
        referenceScreen: event.referenceScreen || '-',
        className: event.affectedClassName || '-',
        tableName: event.tableName || '-',
        recordId: event.recordId || '-',
        recordName: event.recordName || '-'
      };

      if (!event.fieldChanges.length) {
        rows.push({
          rowNo: ++rowNo,
          ...base,
          fieldName: '-',
          screenLabel: '-',
          previousValue: '-',
          newValue: 'No field changes'
        });
        return;
      }

      event.fieldChanges.forEach((change) => {
        rows.push({
          rowNo: ++rowNo,
          ...base,
          fieldName: change.fieldName,
          screenLabel: change.screenLabel,
          previousValue: change.previousValue,
          newValue: change.newValue
        });
      });
    });

    return rows;
  }

  // Creates Report Details and Audit Trail worksheets inside the workbook.
  private buildExcelWorkbook(workbook: ExcelJS.Workbook): void {
    this.buildExcelDetailsSheet(workbook);
    this.buildExcelDataSheet(workbook);
  }

  // Writes generated-on meta and applied filters into the details sheet.
  private buildExcelDetailsSheet(workbook: ExcelJS.Workbook): void {
    const meta = this.getExportMeta();
    const applied = this.getAppliedFiltersForExport();
    const sheet = workbook.addWorksheet('Report Details');

    sheet.mergeCells('A1:B1');
    sheet.getCell('A1').value = 'JSuite Audit Trail Report';
    sheet.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FF0D3B66' } };

    const detailRows: Array<[string, string]> = [
      ['Generated On', meta.generatedOn],
      ['Total Events', String(meta.eventCount)],
      ['Total Field Changes', String(meta.fieldChangeCount)]
    ];

    detailRows.forEach((item) => {
      const row = sheet.addRow(item);
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
      row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
    });

    sheet.addRow([]);
    const filterTitle = sheet.addRow(['Applied Filters']);
    filterTitle.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF0D3B66' } };

    const filterHeader = sheet.addRow(['Filter', 'Value']);
    this.styleExcelHeaderRow(filterHeader);

    applied.forEach((item, index) => {
      const row = sheet.addRow([item.label, item.value]);
      this.styleExcelDataRow(row, index);
      row.getCell(1).font = { bold: true };
    });

    this.autoFitExcelColumns(sheet, [28, 48]);
  }

  // Writes audit field-change rows with wrap-friendly Previous/New value columns.
  private buildExcelDataSheet(workbook: ExcelJS.Workbook): void {
    const sheet = workbook.addWorksheet('Audit Trail', {
      views: [{ state: 'frozen', ySplit: 1 }]
    });

    const headers = [
      'Audit ID',
      'Timestamp',
      'User Name',
      'Action',
      'Module',
      'Screen Name',
      'Reference Screen',
      'Table Name',
      'Database Column',
      'Screen Label',
      'Previous Value',
      'New Value',
      'Record Id',
      'Record Name',
      'Class Name'
    ];

    const headerRow = sheet.addRow(headers);
    this.styleExcelHeaderRow(headerRow);

    const columnWidths = [12, 20, 30, 10, 14, 16, 16, 14, 18, 16, 36, 36, 12, 18, 16];
    const exportRows = this.buildExportRows();
    exportRows.forEach((item, index) => {
      const row = sheet.addRow([
        item.auditId,
        item.timestamp,
        item.user,
        item.action,
        item.moduleName,
        item.screen,
        item.referenceScreen,
        item.tableName,
        item.fieldName,
        item.screenLabel,
        item.previousValue,
        item.newValue,
        item.recordId,
        item.recordName,
        item.className
      ]);
      this.styleExcelDataRow(row, index);
      this.exceljsService.applyCellTextWrap(row, {
        estimateFromValues: [item.previousValue, item.newValue, item.screenLabel],
        minHeight: 18,
        maxHeight: 200
      });

      const previousCell = row.getCell(11);
      const newCell = row.getCell(12);
      previousCell.font = {
        color: {
          argb: this.isNullDisplayValue(item.previousValue) ? 'FF9AA0A6' : 'FFD93025'
        },
        italic: this.isNullDisplayValue(item.previousValue)
      };
      newCell.font = {
        color: { argb: this.isNullDisplayValue(item.newValue) ? 'FF9AA0A6' : 'FF1B5E20' },
        italic: this.isNullDisplayValue(item.newValue)
      };
    });

    this.exceljsService.applyColumnWidths(sheet, columnWidths);
  }

  // Applies the dark-blue header style used on Excel export sheets.
  private styleExcelHeaderRow(row: ExcelJS.Row): void {
    row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    row.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    row.height = 22;
    row.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF0D47A1' }
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFCED4DA' } },
        left: { style: 'thin', color: { argb: 'FFCED4DA' } },
        bottom: { style: 'thin', color: { argb: 'FFCED4DA' } },
        right: { style: 'thin', color: { argb: 'FFCED4DA' } }
      };
    });
  }

  // Applies zebra fill, top wrap alignment, and light borders on data rows.
  private styleExcelDataRow(row: ExcelJS.Row, index: number): void {
    row.alignment = { vertical: 'top', wrapText: true };
    if (index % 2 === 1) {
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF8FBFF' }
        };
      });
    }
    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE9ECEF' } },
        left: { style: 'thin', color: { argb: 'FFE9ECEF' } },
        bottom: { style: 'thin', color: { argb: 'FFE9ECEF' } },
        right: { style: 'thin', color: { argb: 'FFE9ECEF' } }
      };
    });
  }

  // Delegates fixed column widths to the shared ExcelJS helper service.
  private autoFitExcelColumns(sheet: ExcelJS.Worksheet, widths: number[]): void {
    this.exceljsService.applyColumnWidths(sheet, widths);
  }

  // Builds landscape HTML (filters + data table) for print/PDF export.
  private buildCombinedPdfHtmlReport(): string {
    const meta = this.getExportMeta();
    const applied = this.getAppliedFiltersForExport();
    const exportRows = this.buildExportRows();

    const filterRows = applied
      .map(
        (item) => `
        <tr>
          <td class="filter-label">${this.escapeHtml(item.label)}</td>
          <td>${this.escapeHtml(item.value)}</td>
        </tr>`
      )
      .join('');

    const tableRows = exportRows
      .map(
        (row) => `
        <tr>
          <td>${row.rowNo}</td>
          <td>${row.auditId}</td>
          <td>${this.escapeHtml(row.timestamp)}</td>
          <td>${this.escapeHtml(row.user)}</td>
          <td><span class="badge">${this.escapeHtml(row.action)}</span></td>
          <td>${this.escapeHtml(row.moduleName)}</td>
          <td>${this.escapeHtml(row.screen)}</td>
          <td>${this.escapeHtml(row.referenceScreen)}</td>
          <td>${this.escapeHtml(row.tableName)}</td>
          <td class="mono">${this.escapeHtml(row.fieldName)}</td>
          <td>${this.escapeHtml(row.screenLabel)}</td>
          <td class="old value-cell ${this.isNullDisplayValue(row.previousValue) ? 'null' : ''}">
            ${this.escapeHtml(row.previousValue)}
          </td>
          <td class="new value-cell ${this.isNullDisplayValue(row.newValue) ? 'null' : ''}">
            ${this.escapeHtml(row.newValue)}
          </td>
          <td>${this.escapeHtml(row.recordId)}</td>
          <td>${this.escapeHtml(row.recordName)}</td>
          <td>${this.escapeHtml(row.className)}</td>
        </tr>`
      )
      .join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Audit Trail Report</title>
          <style>
            @page { size: A4 landscape; margin: 8mm; }
            * { box-sizing: border-box; }
            body {
              font-family: "Segoe UI", Arial, sans-serif;
              color: #212529;
              margin: 0;
              padding: 12px;
              background: #fff;
            }
            .report-header {
              border-bottom: 2px solid #0d47a1;
              padding-bottom: 8px;
              margin-bottom: 12px;
            }
            .report-header h1 {
              margin: 0 0 6px;
              color: #0d3b66;
              font-size: 18px;
            }
            .meta {
              display: grid;
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 4px 12px;
              font-size: 11px;
            }
            h2 {
              margin: 14px 0 8px;
              color: #0d3b66;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9.5px;
              table-layout: fixed;
            }
            th, td {
              border: 1px solid #dee2e6;
              padding: 4px 5px;
              vertical-align: top;
              text-align: left;
              word-wrap: break-word;
              overflow-wrap: anywhere;
            }
            th {
              background: #0d47a1;
              color: #fff;
              font-weight: 700;
            }
            tbody tr:nth-child(even) { background: #f8fbff; }
            .filters-table { width: 60%; margin-bottom: 8px; table-layout: auto; }
            .filter-label { font-weight: 700; width: 35%; }
            .badge {
              display: inline-block;
              padding: 1px 6px;
              border-radius: 8px;
              background: #e7f6ec;
              color: #1b7a3d;
              font-weight: 700;
              font-size: 9px;
            }
            .old { color: #d93025; font-weight: 600; white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere; font-family: Consolas, "Courier New", monospace; font-size: 8px; }
            .new { color: #1b5e20; font-weight: 600; white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere; font-family: Consolas, "Courier New", monospace; font-size: 8px; }
            .null { color: #9aa0a6 !important; font-style: italic; font-weight: 400; }
            .mono { font-family: Consolas, "Courier New", monospace; font-size: 9px; }
            .value-cell { max-width: 0; }
            @media print {
              body { padding: 0; }
              thead { display: table-header-group; }
              tr { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <header class="report-header">
            <h1>JSuite Audit Trail Report</h1>
            <div class="meta">
              <div><strong>Generated On:</strong> ${this.escapeHtml(meta.generatedOn)}</div>
              <div><strong>Audit Events:</strong> ${meta.eventCount}</div>
              <div><strong>Field Changes:</strong> ${meta.fieldChangeCount}</div>
            </div>
          </header>

          <h2>Applied Filters</h2>
          <table class="filters-table">
            <thead>
              <tr>
                <th>Filter</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              ${filterRows}
            </tbody>
          </table>

          <h2>Event Details &amp; Field Level Changes</h2>
          <table>
            <thead>
              <tr>
                <th style="width:3%">SINo</th>
                <th style="width:5%">Audit ID</th>
                <th style="width:8%">Timestamp</th>
                <th style="width:10%">User Name</th>
                <th style="width:5%">Action</th>
                <th style="width:6%">Module</th>
                <th style="width:7%">Screen Name</th>
                <th style="width:7%">Reference Screen</th>
                <th style="width:6%">Table Name</th>
                <th style="width:7%">Database Column</th>
                <th style="width:6%">Screen Label</th>
                <th style="width:10%">Previous Value</th>
                <th style="width:10%">New Value</th>
                <th style="width:5%">Record Id</th>
                <th style="width:7%">Record Name</th>
                <th style="width:6%">Class Name</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>`;
  }

  // Collects generated-on timestamp and event/field-change counts for exports.
  private getExportMeta(): {
    generatedOn: string;
    dateRange: string;
    eventCount: number;
    fieldChangeCount: number;
  } {
    const from = this.datePipe.transform(this.filters.fromDate, 'dd-MMM-yyyy') || '-';
    const to = this.datePipe.transform(this.filters.toDate, 'dd-MMM-yyyy') || '-';
    const fieldChangeCount = this.filteredEvents.reduce(
      (total, event) => total + (event.fieldChanges?.length || 0),
      0
    );

    return {
      generatedOn: this.formatTimestamp(new Date().toISOString()),
      dateRange: `${from} to ${to}`,
      eventCount: this.filteredEvents.length,
      fieldChangeCount
    };
  }

  // Lists the active filter values shown on Excel/PDF detail sections.
  private getAppliedFiltersForExport(): Array<{ label: string; value: string }> {
    const from = this.datePipe.transform(this.filters.fromDate, 'dd-MMM-yyyy') || '-';
    const to = this.datePipe.transform(this.filters.toDate, 'dd-MMM-yyyy') || '-';
    const userLabel = this.filters.user?.trim() || 'All Users';
    const actionLabel = this.filters.action?.trim()
      ? this.formatActionLabel(this.filters.action)
      : 'All Actions';

    return [
      { label: 'From Date', value: from },
      { label: 'To Date', value: to },
      { label: 'User', value: userLabel },
      { label: 'Action', value: actionLabel },
      { label: 'Language', value: this.getSelectedLanguageLabel() }
    ];
  }

  // Builds a compact timestamp string for export file names.
  private getExportFileStamp(): string {
    const now = new Date();
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(
      now.getHours()
    )}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  }

  // Lets the user show/hide grid columns, then refreshes visibility and layout.
  openColumnsDialog(): void {
    const dialogRef = this.dialog.open(ColumnsDialogComponent, {
      width: '420px',
      data: {
        selectedFields: [...this.selectedFields],
        toggleableCols: this.toggleableCols
      }
    });

    dialogRef.afterClosed().subscribe((selected: string[] | undefined) => {
      if (!selected) {
        return;
      }
      this.selectedFields = selected;
      this.applyColumnVisibility();
    });
  }

  // Updates AG Grid page size and refreshes custom pagination state.
  onPageSizeChanged(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.setGridOption('paginationPageSize', Number(this.pageSize));
    this.gridApi.paginationGoToFirstPage();
    this.updatePaginationState();
  }

  // Jumps the grid pager to the first page.
  goToFirstPage(): void {
    this.gridApi?.paginationGoToFirstPage();
    this.updatePaginationState();
  }

  // Moves the grid pager one page backward.
  goToPreviousPage(): void {
    this.gridApi?.paginationGoToPreviousPage();
    this.updatePaginationState();
  }

  // Moves the grid pager one page forward.
  goToNextPage(): void {
    this.gridApi?.paginationGoToNextPage();
    this.updatePaginationState();
  }

  // Jumps the grid pager to the last page.
  goToLastPage(): void {
    this.gridApi?.paginationGoToLastPage();
    this.updatePaginationState();
  }

  // Keeps local start/end/total pager fields in sync with AG Grid pagination.
  onPaginationChanged(): void {
    if (this.gridApi) {
      this.pageSize = this.gridApi.paginationGetPageSize() || 10;
    }
    this.updatePaginationState();
  }

  // Maps CREATE/UPDATE/DELETE to CSS badge classes for grid rendering.
  getActionBadgeClass(action: string): string {
    const normalized = (action || '').toUpperCase();
    if (normalized === 'CREATE') {
      return 'badge-action badge-create';
    }
    if (normalized === 'UPDATE') {
      return 'badge-action badge-update';
    }
    if (normalized === 'DELETE') {
      return 'badge-action badge-delete';
    }
    return 'badge-action';
  }

  // Normalizes action text to uppercase for consistent UI and exports.
  formatActionLabel(action: string): string {
    return (action || '-').toUpperCase();
  }

  // Treats literal NULL as empty so Previous/New can use muted styling.
  isNullDisplayValue(value: string): boolean {
    return (value || '').toUpperCase() === 'NULL';
  }

  // Calls reteriveAuditInRange with date/user/action/language and maps the response.
  private loadAuditEvents(showMessage: boolean): void {
    const fromDate = this.datePipe.transform(this.filters.fromDate, 'yyyy-MM-dd');
    const toDate = this.datePipe.transform(this.filters.toDate, 'yyyy-MM-dd');

    if (!fromDate || !toDate) {
      this.showValidationMessage('From Date and To Date are required.');
      return;
    }

    if (this.isToDateBeforeFromDate()) {
      this.showValidationMessage('To Date must not be older than From Date.');
      return;
    }

    // Cancel any in-flight audit call so an older response cannot stop the newer load's spinner.
    this.auditLoadSubscription?.unsubscribe();
    this.spinner.start(this.auditLoadTaskId);

    const userFilter = this.filters.user?.trim() || 'allUsers';
    const actionFilter = this.filters.action?.trim()
      ? this.filters.action.trim().toLowerCase()
      : 'allActions';

    this.auditLoadSubscription = this.auditTrailService
      .retrieveauditInRange(
        fromDate,
        toDate,
        this.selectedLanguage,
        userFilter,
        actionFilter
      )
      .pipe(
        // Always clears this task id: success, HTTP error, unsubscribe, or destroy.
        finalize(() => this.spinner.stop(this.auditLoadTaskId))
      )
      .subscribe({
        next: (response: any) => {
          // AuthInterceptor converts many HTTP failures into next(of(error)).
          if (this.isHttpFailurePayload(response)) {
            this.handleAuditLoadFailure(response);
            return;
          }

          if (response?.error === true) {
            this.allEvents = [];
            this.refreshFilterOptions();
            this.applyClientFilters(false);
            this.snackBar.open(response.statusMessage || 'Failed to load audit records.', 'OK', {
              duration: 5000
            });
            return;
          }

          const records = this.normalizeApiResponse(response);
          this.allEvents = records.map((record) => this.mapApiRecordToEvent(record));
          this.refreshFilterOptions();
          this.applyClientFilters(showMessage);
        },
        error: (err) => {
          // Path used when interceptor rethrows (e.g. status 0 / 404 after handling).
          this.handleAuditLoadFailure(err);
        }
      });
  }

  // Detects HttpErrorResponse payloads emitted via next(of(error)) by the interceptor.
  private isHttpFailurePayload(response: unknown): boolean {
    if (!response || typeof response !== 'object') {
      return false;
    }
    const payload = response as { name?: string; status?: number };
    return (
      response instanceof HttpErrorResponse ||
      payload.name === 'HttpErrorResponse' ||
      payload.status === 0 ||
      (typeof payload.status === 'number' && payload.status >= 400)
    );
  }

  // Clears grid data and shows a load-failure snackbar when appropriate.
  private handleAuditLoadFailure(err: unknown): void {
    this.allEvents = [];
    this.refreshFilterOptions();
    this.applyClientFilters(false);

    const status =
      err instanceof HttpErrorResponse
        ? err.status
        : typeof (err as { status?: number })?.status === 'number'
          ? (err as { status: number }).status
          : null;

    // 401 / connection messages are already shown by AuthInterceptor — avoid duplicate snackbars.
    if (status === 401 || status === 0 || status === 404) {
      return;
    }

    this.snackBar.open('Unable to retrieve audit records.', 'OK', { duration: 5000 });
  }

  // Accepts either a raw array or a { data: [] } wrapper from the API.
  private normalizeApiResponse(response: any): AuditTrailApiRecord[] {
    if (Array.isArray(response)) {
      return response as AuditTrailApiRecord[];
    }
    if (Array.isArray(response?.data)) {
      return response.data as AuditTrailApiRecord[];
    }
    return [];
  }

  // Maps one API audit record into the UI AuditEvent model with field changes.
  private mapApiRecordToEvent(record: AuditTrailApiRecord): AuditEvent {
    const action = this.normalizeAction(record.actionType);
    const fieldChanges = this.buildFieldChanges(
      record.modified_fields,
      record.screenlabel,
      action
    );
    const recordId = record.affectedRecordId != null ? String(record.affectedRecordId) : '';
    const screenName = (record.screenName || '').trim();
    const referenceScreen = (record.referenceScreen || '').trim();
    const moduleName = (record.moduleName || '').trim();
    // For record-name fallback only: screenName preferred, else referenceScreen
    const displayScreen = screenName || referenceScreen;
    const recordName = this.buildRecordName(displayScreen, recordId, fieldChanges);

    return {
      auditId: record.id,
      timestampRaw: record.createdAt,
      timestamp: this.formatTimestamp(record.createdAt),
      user: record.createdBy || '',
      action,
      affectedClassName: record.affectedClassName || '',
      recordId,
      screenName,
      referenceScreen,
      moduleName,
      displayScreen,
      tableName: record.tableName || '',
      databaseColumn: record.databaseColumn || '',
      screenLabel: (record.screenlabel || '').trim(),
      recordName,
      modifiedFieldsRaw: record.modified_fields || '',
      changedFields: fieldChanges.length,
      fieldChanges
    };
  }

  // Uppercases backend actionType so filters and badges stay consistent.
  private normalizeAction(actionType: string | null | undefined): string {
    const normalized = (actionType || '').trim().toUpperCase();
    return normalized || '-';
  }

  /**
   * Builds Field Level Changes rows used by UI, Excel, and PDF.
   *
   * Backend sends two parallel strings:
   * - modified_fields → DB column names (emailbody, tenantId, …)
   * - screenlabel     → localized labels (本文, テナントID, …) in the SAME order
   *
   * We parse both with the same structure-aware parser, then map label → column
   * by name when possible, otherwise by index (needed for non-English labels).
   */
  private buildFieldChanges(
    modifiedFields: string | null,
    screenLabel: string | null,
    actionType: string
  ): AuditFieldChange[] {
    const technicalPairs = this.parseChangeEntries(modifiedFields);
    const labelPairs = this.parseChangeEntries(screenLabel);
    const isCreate = actionType.toUpperCase() === 'CREATE';
    // Index alignment is safe only when both sides have the same field count
    // and every screenlabel key looks like a real label (not HTML/JSON junk).
    const canAlignByIndex =
      labelPairs.length > 0 &&
      labelPairs.length === technicalPairs.length &&
      labelPairs.every((pair) => this.isPlausibleFieldKey(pair.key));

    return technicalPairs.map((pair, index) => {
      const labelPair = this.findMatchingLabelPair(
        pair.key,
        index,
        labelPairs,
        canAlignByIndex
      );
      const { previousValue, newValue } = this.resolveChangeValues(pair.value, isCreate);

      return {
        fieldName: pair.key,
        // Prefer backend screenlabel key; fall back to English-ized DB column name.
        screenLabel:
          labelPair && this.isPlausibleFieldKey(labelPair.key)
            ? labelPair.key
            : this.toDisplayLabel(pair.key),
        previousValue: this.displayValue(previousValue),
        newValue: this.displayValue(newValue)
      };
    });
  }

  /**
   * Maps one DB column to its screen label.
   * 1) Same normalized name (works for English labels like "Tenant Id" ≈ tenantId)
   * 2) Same list position when counts match (works for Japanese: tenantId → テナントID)
   */
  private findMatchingLabelPair(
    fieldName: string,
    index: number,
    labelPairs: Array<{ key: string; value: string }>,
    canAlignByIndex: boolean
  ): { key: string; value: string } | undefined {
    const plausiblePairs = labelPairs.filter((pair) => this.isPlausibleFieldKey(pair.key));
    if (!plausiblePairs.length) {
      return undefined;
    }

    const normalizedField = this.normalizeKey(fieldName);
    const byKey = plausiblePairs.find(
      (pair) => this.normalizeKey(pair.key) === normalizedField
    );
    if (byKey) {
      return byKey;
    }

    if (canAlignByIndex && index >= 0 && index < plausiblePairs.length) {
      return plausiblePairs[index];
    }

    return undefined;
  }

  /**
   * Returns true only for real field / screen-label keys.
   * WHY: huge Previous/New values (JSON or HTML email) contain commas and "=".
   * Without this check those fragments become fake keys and spill into the next row.
   * HOW: allow short Unicode labels; reject markup/JSON punctuation and long slices.
   */
  private isPlausibleFieldKey(key: string): boolean {
    const trimmed = (key || '').trim();
    // Real labels are short (本文, lastModifiedDate); HTML/JSON slices are long.
    if (!trimmed || trimmed.length > 80) {
      return false;
    }
    // Reject JSON braces/quotes and HTML tags/attrs (e.g. <br/>, border=, style:)
    if (/[{}\[\]"'=<>,:/\\]/.test(trimmed)) {
      return false;
    }
    // Require at least one letter/digit (ASCII or Japanese/other BMP letters)
    if (!/[0-9A-Za-z\u00C0-\uFFFF]/.test(trimmed)) {
      return false;
    }
    return true;
  }

  /**
   * Chooses parser by backend format:
   * - update: "field=old changes into new,…"
   * - create: "field=value,field2=value2" (no changes into)
   */
  private parseChangeEntries(
    raw: string | null | undefined
  ): Array<{ key: string; value: string }> {
    if (!raw || !raw.trim()) {
      return [];
    }

    const text = raw.trim();
    if (/\schanges\s+into\s+/i.test(text)) {
      return this.parseChangesIntoFieldEntries(text);
    }

    return this.parseSimpleEqualsEntries(text);
  }

  /**
   * Parses update entries: key=previous changes into new,nextKey=…
   *
   * WHY not split on every comma?
   *   JSON and HTML values contain commas (and "=" in attributes). A naive split
   *   breaks one field across Screen Label / Previous / New on the next row.
   *
   * HOW this stays safe:
   *   - Key = text until first depth-0 "=" (Unicode keys OK: テナントID, 本文)
   *   - Previous ends at depth-0 " changes into "
   *   - New ends only at a comma that truly starts another key=… changes into …
   *   - Brace/quote depth ignores commas inside JSON strings/objects
   *   - isPlausibleFieldKey + 80-char key limit ignore HTML false boundaries
   */
  private parseChangesIntoFieldEntries(
    text: string
  ): Array<{ key: string; value: string }> {
    const results: Array<{ key: string; value: string }> = [];
    let pos = 0;

    while (pos < text.length) {
      pos = this.skipListSeparators(text, pos);
      if (pos >= text.length) {
        break;
      }

      const equalsIndex = this.findFirstEqualsAtDepthZero(text, pos);
      if (equalsIndex < 0) {
        break;
      }

      const key = text.slice(pos, equalsIndex).trim();
      if (!this.isPlausibleFieldKey(key)) {
        // Skip corrupt/HTML fragment; move past "=" so we do not loop forever
        pos = equalsIndex + 1;
        continue;
      }

      const previousStart = equalsIndex + 1;
      const intoIndex = this.findChangesIntoAtDepthZero(text, previousStart);
      if (intoIndex < 0) {
        // No "changes into" — treat remaining as a plain value for this key
        const { value, nextIndex } = this.readValueUntilTopLevelComma(text, previousStart);
        results.push({ key, value });
        pos = nextIndex;
        continue;
      }

      const intoMatch = text.slice(intoIndex).match(/^\s+changes\s+into\s+/i);
      if (!intoMatch) {
        break;
      }

      const previousValue = text.slice(previousStart, intoIndex).trim();
      const newValueStart = intoIndex + intoMatch[0].length;
      // Stop before the next real field (not before a comma inside JSON/HTML)
      const boundary = this.findNextChangesIntoFieldBoundary(text, newValueStart);
      const newValue = text.slice(newValueStart, boundary).replace(/,\s*$/, '').trim();

      results.push({
        key,
        value: `${previousValue} changes into ${newValue}`
      });
      pos = boundary;
    }

    return results;
  }

  /**
   * Parses create-style lists: key=value,key2=value2
   * Uses the same Unicode key + depth-aware value rules as the update parser.
   */
  private parseSimpleEqualsEntries(
    text: string
  ): Array<{ key: string; value: string }> {
    const results: Array<{ key: string; value: string }> = [];
    let pos = 0;

    while (pos < text.length) {
      pos = this.skipListSeparators(text, pos);
      if (pos >= text.length) {
        break;
      }

      const equalsIndex = this.findFirstEqualsAtDepthZero(text, pos);
      if (equalsIndex < 0) {
        break;
      }

      const key = text.slice(pos, equalsIndex).trim();
      if (!this.isPlausibleFieldKey(key)) {
        pos = equalsIndex + 1;
        continue;
      }

      const { value, nextIndex } = this.readValueUntilTopLevelComma(text, equalsIndex + 1);
      results.push({ key, value });
      pos = nextIndex;
    }

    return results;
  }

  /** Skips commas/spaces between list entries. */
  private skipListSeparators(text: string, start: number): number {
    let i = start;
    while (i < text.length && (text[i] === ',' || /\s/.test(text[i]))) {
      i++;
    }
    return i;
  }

  /**
   * Finds the "=" that separates key from value.
   * Ignores "=" inside quotes or inside { } / [ ] (JSON), so values stay intact.
   */
  private findFirstEqualsAtDepthZero(text: string, start: number): number {
    let depth = 0;
    let inString = false;
    let stringQuote = '';
    let escape = false;

    for (let i = start; i < text.length; i++) {
      const ch = text[i];

      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === '\\') {
          escape = true;
        } else if (ch === stringQuote) {
          inString = false;
        }
        continue;
      }

      if (ch === '"' || ch === "'") {
        inString = true;
        stringQuote = ch;
        continue;
      }
      if (ch === '{' || ch === '[') {
        depth++;
        continue;
      }
      if (ch === '}' || ch === ']') {
        depth = Math.max(0, depth - 1);
        continue;
      }
      if (ch === ',' && depth === 0) {
        return -1;
      }
      if (ch === '=' && depth === 0) {
        return i;
      }
    }

    return -1;
  }

  /**
   * Finds " changes into " that is outside quotes / JSON braces.
   * Avoids splitting when that phrase somehow appears inside a JSON string value.
   */
  private findChangesIntoAtDepthZero(text: string, start: number): number {
    const re = /\s+changes\s+into\s+/gi;
    re.lastIndex = start;
    let match: RegExpExecArray | null;

    while ((match = re.exec(text)) !== null) {
      if (match.index < start) {
        continue;
      }
      if (this.isRegionAtDepthZero(text, start, match.index)) {
        return match.index;
      }
    }

    return -1;
  }

  /**
   * Finds where the current New Value ends.
   * Only a top-level comma that starts another real field (key=… changes into …)
   * is a boundary — commas in JSON or "Best Regards," in HTML are ignored.
   */
  private findNextChangesIntoFieldBoundary(text: string, newValueStart: number): number {
    let depth = 0;
    let inString = false;
    let stringQuote = '';
    let escape = false;

    for (let i = newValueStart; i < text.length; i++) {
      const ch = text[i];

      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === '\\') {
          escape = true;
        } else if (ch === stringQuote) {
          inString = false;
        }
        continue;
      }

      if (ch === '"' || ch === "'") {
        inString = true;
        stringQuote = ch;
        continue;
      }
      if (ch === '{' || ch === '[') {
        depth++;
        continue;
      }
      if (ch === '}' || ch === ']') {
        depth = Math.max(0, depth - 1);
        continue;
      }

      if (ch === ',' && depth === 0 && this.looksLikeChangesIntoFieldStart(text, i + 1)) {
        return i;
      }
    }

    return text.length;
  }

  /**
   * Checks whether text after a comma is a new audit field.
   * Guards against HTML false positives like: "Best Regards,<br/>… border=…"
   * by requiring a short plausible key and a following "changes into".
   */
  private looksLikeChangesIntoFieldStart(text: string, start: number): boolean {
    const pos = this.skipListSeparators(text, start);
    const equalsIndex = this.findFirstEqualsAtDepthZero(text, pos);
    if (equalsIndex < 0) {
      return false;
    }
    // Real keys are near the comma; a far "=" is usually HTML/JSON content (border=).
    if (equalsIndex - pos > 80) {
      return false;
    }
    const key = text.slice(pos, equalsIndex).trim();
    if (!this.isPlausibleFieldKey(key)) {
      return false;
    }
    return this.findChangesIntoAtDepthZero(text, equalsIndex + 1) >= 0;
  }

  /** True when [from, until) stays at brace depth 0 and outside quoted strings. */
  private isRegionAtDepthZero(text: string, from: number, until: number): boolean {
    let depth = 0;
    let inString = false;
    let stringQuote = '';
    let escape = false;

    for (let i = from; i < until; i++) {
      const ch = text[i];

      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === '\\') {
          escape = true;
        } else if (ch === stringQuote) {
          inString = false;
        }
        continue;
      }

      if (ch === '"' || ch === "'") {
        inString = true;
        stringQuote = ch;
        continue;
      }
      if (ch === '{' || ch === '[') {
        depth++;
        continue;
      }
      if (ch === '}' || ch === ']') {
        depth = Math.max(0, depth - 1);
        continue;
      }
    }

    return depth === 0 && !inString;
  }

  /**
   * Reads a create-style value until the next top-level comma
   * (commas inside JSON braces/quotes do not end the value).
   */
  private readValueUntilTopLevelComma(
    text: string,
    start: number
  ): { value: string; nextIndex: number } {
    let i = start;
    let depth = 0;
    let inString = false;
    let stringQuote = '';
    let escape = false;

    while (i < text.length) {
      const ch = text[i];

      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === '\\') {
          escape = true;
        } else if (ch === stringQuote) {
          inString = false;
        }
        i++;
        continue;
      }

      if (ch === '"' || ch === "'") {
        inString = true;
        stringQuote = ch;
        i++;
        continue;
      }

      if (ch === '{' || ch === '[') {
        depth++;
        i++;
        continue;
      }

      if (ch === '}' || ch === ']') {
        depth = Math.max(0, depth - 1);
        i++;
        continue;
      }

      if (ch === ',' && depth === 0) {
        return { value: text.slice(start, i).trim(), nextIndex: i };
      }

      i++;
    }

    return { value: text.slice(start, i).trim(), nextIndex: i };
  }

  /**
   * Splits one field's raw value into Previous / New.
   * Uses depth-0 "changes into" so JSON/HTML content cannot fake the delimiter.
   */
  private resolveChangeValues(
    rawValue: string,
    isCreate: boolean
  ): { previousValue: string; newValue: string } {
    const value = (rawValue || '').trim();
    if (!value) {
      return { previousValue: 'NULL', newValue: 'NULL' };
    }

    const intoIndex = this.findChangesIntoAtDepthZero(value, 0);
    if (intoIndex >= 0) {
      const intoMatch = value.slice(intoIndex).match(/^\s+changes\s+into\s+/i);
      if (intoMatch) {
        return {
          previousValue: value.slice(0, intoIndex).trim(),
          newValue: value.slice(intoIndex + intoMatch[0].length).trim()
        };
      }
    }

    // Alternate format: "old -> new"
    if (value.includes('->')) {
      const arrowIndex = value.indexOf('->');
      return {
        previousValue: value.slice(0, arrowIndex).trim() || 'NULL',
        newValue: value.slice(arrowIndex + 2).trim() || ''
      };
    }

    // Create-style: only the new value is present
    if (isCreate) {
      return { previousValue: 'NULL', newValue: value };
    }

    return { previousValue: '-', newValue: value };
  }

  // Converts null/empty to NULL and pretty-prints JSON for cell display.
  private displayValue(value: string): string {
    const trimmed = (value || '').trim();
    if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
      return 'NULL';
    }
    return this.prettyPrintIfJson(trimmed);
  }

  // Formats JSON strings with indentation while keeping them in a single cell.
  private prettyPrintIfJson(value: string): string {
    const trimmed = value.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
      return value;
    }
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }

  // Derives a friendly record name from name fields or screen + record id.
  private buildRecordName(
    screenName: string,
    recordId: string,
    fieldChanges: AuditFieldChange[]
  ): string {
    const nameChange = fieldChanges.find((change) =>
      /name/i.test(change.fieldName) || /name/i.test(change.screenLabel)
    );

    if (nameChange?.newValue && nameChange.newValue !== 'NULL') {
      return nameChange.newValue;
    }

    if (screenName && recordId) {
      return `${screenName} #${recordId}`;
    }

    return screenName || (recordId ? `Record #${recordId}` : '-');
  }

  // Strips non-alphanumerics for case-insensitive field/label key matching.
  private normalizeKey(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Turns camelCase/snake_case DB column names into readable Screen Label text.
  private toDisplayLabel(fieldName: string): string {
    return fieldName
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  // Formats createdAt into the DD-MMM-YYYY HH:mm:ss display used in the grid.
  private formatTimestamp(value: string): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = date.getDate().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  }

  // Rebuilds Module/Screen/Table Class dropdown options from loaded events.
  private refreshFilterOptions(): void {
    // User list comes from retrieveAllUsersByWorkspace — do not overwrite it here.
    // Action options are fixed uppercase CREATE/UPDATE/DELETE — do not rebuild from events.
    this.classOptions = this.buildOptions(
      this.allEvents.map((event) => event.affectedClassName),
      'All Classes'
    );
    this.screenOptions = this.buildOptions(
      this.allEvents.map((event) => event.screenName),
      'All Screens'
    );
    this.moduleOptions = this.buildOptions(
      this.allEvents.map((event) => event.moduleName),
      'All Modules'
    );
    this.tableOptions = this.buildOptions(
      this.allEvents.map((event) => event.tableName),
      'All Tables'
    );
  }

  // Builds All + sorted unique select options for filter dropdowns.
  private buildOptions(values: string[], allLabel: string): SelectOption[] {
    const unique = Array.from(
      new Set(values.map((value) => (value || '').trim()).filter((value) => !!value))
    ).sort((a, b) => a.localeCompare(b));

    return [{ label: allLabel, value: '' }, ...unique.map((value) => ({ label: value, value }))];
  }

  // Applies client-side filters, refreshes grid rows, and selects the first match.
  private applyClientFilters(showMessage: boolean): void {
    this.filteredEvents = this.allEvents.filter((event) => {
      if (
        this.filters.user &&
        event.user.toLowerCase() !== this.filters.user.trim().toLowerCase()
      ) {
        return false;
      }
      if (this.filters.affectedClassName && event.affectedClassName !== this.filters.affectedClassName) {
        return false;
      }
      if (
        this.filters.action &&
        event.action.toUpperCase() !== this.filters.action.trim().toUpperCase()
      ) {
        return false;
      }
      // Module / Screen Name dropdowns are showcase-only for now — not applied on Search.
      // if (this.filters.screenName && event.screenName !== this.filters.screenName) {
      //   return false;
      // }
      // if (this.filters.moduleName && event.moduleName !== this.filters.moduleName) {
      //   return false;
      // }
      if (this.filters.tableName && event.tableName !== this.filters.tableName) {
        return false;
      }
      if (
        this.filters.recordName &&
        !`${event.recordName} ${event.screenLabel}`
          .toLowerCase()
          .includes(this.filters.recordName.trim().toLowerCase())
      ) {
        return false;
      }
      if (
        this.filters.recordId &&
        !event.recordId.toLowerCase().includes(this.filters.recordId.trim().toLowerCase())
      ) {
        return false;
      }
      return true;
    });

    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.filteredEvents);
      this.gridApi.paginationGoToFirstPage();
      this.updatePaginationState();
      this.refreshGridColumnLayout(true);
    }

    if (this.filteredEvents.length) {
      this.selectEvent(this.filteredEvents[0], true);
    } else {
      this.selectedEvent = null;
    }

    if (showMessage) {
      this.snackBar.open(`Found ${this.filteredEvents.length} audit event(s).`, 'Close', {
        duration: 5000
      });
    }
  }

  // Sets the selected event for details and optionally syncs AG Grid row selection.
  private selectEvent(event: AuditEvent, syncGridSelection: boolean): void {
    this.selectedEvent = event;
    this.detailsExpanded = true;

    if (syncGridSelection && this.gridApi) {
      this.gridApi.forEachNode((node) => {
        const matches = node.data?.auditId === event.auditId;
        node.setSelected(matches);
        if (matches) {
          this.gridApi.ensureNodeVisible(node, 'middle');
        }
      });
    }
  }

  // Defines AG Grid columns, default hidden fields, and Columns-dialog field list.
  private initColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: '#',
        colId: 'rowIndex',
        valueGetter: (params) => (params.node ? params.node.rowIndex! + 1 : ''),
        width: 70,
        minWidth: 70,
        maxWidth: 80,
        sortable: false,
        filter: false,
        wrapText: false,
        autoHeight: false,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        ...this.jsignalService.autoTooltip('#')
      },
      {
        headerName: 'Timestamp',
        field: 'timestamp',
        width: 180,
        minWidth: 160,
        maxWidth: 220,
        sort: 'desc',
        ...this.jsignalService.autoTooltip('Timestamp')
      },
      {
        headerName: 'User Name',
        field: 'user',
        width: 240,
        minWidth: 180,
        maxWidth: 320,
        ...this.jsignalService.autoTooltip('User Name')
      },
      {
        headerName: 'Action',
        field: 'action',
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        wrapText: false,
        autoHeight: false,
        ...this.jsignalService.autoTooltip('Action'),
        cellRenderer: (params: ValueFormatterParams) =>
          this.renderBadge(
            this.formatActionLabel(params.value),
            this.getActionBadgeClass(params.value)
          )
      },
      {
        headerName: 'Module',
        field: 'moduleName',
        width: 160,
        minWidth: 120,
        maxWidth: 240,
        valueFormatter: (params) => params.value || '-',
        ...this.jsignalService.autoTooltip('Module')
      },
      {
        headerName: 'Screen Name',
        field: 'screenName',
        width: 180,
        minWidth: 140,
        maxWidth: 280,
        valueFormatter: (params) => params.value || '-',
        ...this.jsignalService.autoTooltip('Screen Name')
      },
      {
        headerName: 'Reference Screen',
        field: 'referenceScreen',
        width: 190,
        minWidth: 140,
        maxWidth: 280,
        valueFormatter: (params) => params.value || '-',
        ...this.jsignalService.autoTooltip('Reference Screen')
      },
      {
        headerName: 'Class Name',
        field: 'affectedClassName',
        width: 160,
        minWidth: 120,
        maxWidth: 260,
        hide: true,
        ...this.jsignalService.autoTooltip('Class Name')
      },
      {
        headerName: 'Table Name',
        field: 'tableName',
        width: 180,
        minWidth: 120,
        maxWidth: 260,
        ...this.jsignalService.autoTooltip('Table Name')
      },
      {
        headerName: 'Record ID',
        field: 'recordId',
        width: 120,
        minWidth: 100,
        maxWidth: 180,
        hide: true,
        ...this.jsignalService.autoTooltip('Record ID')
      },
      {
        headerName: 'Record Name',
        field: 'recordName',
        width: 200,
        minWidth: 140,
        maxWidth: 360,
        hide: true,
        ...this.jsignalService.autoTooltip('Record Name')
      },
      {
        headerName: 'Changed Fields',
        field: 'changedFields',
        width: 140,
        minWidth: 120,
        maxWidth: 160,
        wrapText: false,
        autoHeight: false,
        cellClass: 'changed-fields-cell',
        ...this.jsignalService.autoTooltip('Changed Fields'),
        cellRenderer: (params: ValueFormatterParams) => {
          const count = Number(params.value || 0);
          return `<span class="changed-fields-link">${count}</span>`;
        }
      }
    ];

    this.toggleableCols = this.columnDefs
      .filter((col) => !!col.field)
      .map((col) => ({
        headerName: col.headerName as string,
        field: col.field as string
      }));

    this.selectedFields = this.toggleableCols
      .map((col) => col.field)
      .filter((field) => !this.defaultHiddenFields.includes(field));
  }

  // Shows/hides columns from selectedFields and recalculates column layout.
  private applyColumnVisibility(): void {
    if (!this.gridApi) {
      return;
    }

    this.toggleableCols.forEach((col) => {
      const visible = this.selectedFields.includes(col.field);
      this.gridApi.setColumnsVisible([col.field], visible);
    });

    this.refreshGridColumnLayout(true);
  }

  // Auto-sizes columns from content, caps width for wrap, avoids overlap squeeze.
  private refreshGridColumnLayout(forceAutoSize: boolean): void {
    if (!this.gridApi || this.refreshingColumns) {
      return;
    }

    if (this.columnLayoutTimer) {
      clearTimeout(this.columnLayoutTimer);
    }

    this.columnLayoutTimer = setTimeout(() => {
      if (!this.gridApi || this.refreshingColumns) {
        return;
      }

      const visibleCols = (this.gridApi.getColumns() || []).filter((col) => col.isVisible());
      if (!visibleCols.length) {
        return;
      }

      this.refreshingColumns = true;
      try {
        if (forceAutoSize) {
          this.gridApi.autoSizeColumns(
            visibleCols.map((col) => col.getColId()),
            false
          );

          this.gridApi.applyColumnState({
            state: visibleCols.map((col) => {
              const def = col.getColDef();
              const minWidth = def.minWidth ?? 80;
              const maxWidth = def.maxWidth ?? 360;
              const actual = col.getActualWidth();
              return {
                colId: col.getColId(),
                width: Math.min(maxWidth, Math.max(minWidth, actual))
              };
            })
          });
        }

        const usedWidth = visibleCols.reduce((sum, col) => sum + col.getActualWidth(), 0);
        const availableWidth = this.getAuditGridBodyWidth();

        // Extra room → fill grid. Too many / wide columns → keep widths + horizontal scroll.
        if (availableWidth > 0 && usedWidth < availableWidth - 12) {
          this.gridApi.sizeColumnsToFit({
            defaultMinWidth: 80
          });
        }

        this.gridApi.resetRowHeights();
      } finally {
        this.refreshingColumns = false;
      }
    }, 50);
  }

  // Reads the AG Grid body viewport width used for fit-vs-scroll decisions.
  private getAuditGridBodyWidth(): number {
    const viewport = document.querySelector(
      '.audit-grid .ag-center-cols-viewport, .audit-grid .ag-body-viewport'
    ) as HTMLElement | null;
    return viewport?.clientWidth || 0;
  }

  // Syncs total/current page and showing range from AG Grid or fallback math.
  private updatePaginationState(): void {
    if (!this.gridApi) {
      this.totalRows = this.filteredEvents.length;
      this.totalPages = Math.max(1, Math.ceil(this.totalRows / this.pageSize));
      this.currentPage = 1;
      this.startRow = this.totalRows ? 1 : 0;
      this.endRow = Math.min(this.pageSize, this.totalRows);
      return;
    }

    this.totalRows = this.gridApi.paginationGetRowCount();
    this.totalPages = Math.max(1, this.gridApi.paginationGetTotalPages());
    this.currentPage = this.gridApi.paginationGetCurrentPage() + 1;
    const pageSize = this.gridApi.paginationGetPageSize();
    this.startRow = this.totalRows ? (this.currentPage - 1) * pageSize + 1 : 0;
    this.endRow = Math.min(this.currentPage * pageSize, this.totalRows);
  }

  // Returns blank filters with From/To defaulted to today start/end.
  private createDefaultFilters(): AuditFilterState {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    return {
      fromDate: start,
      toDate: end,
      user: '',
      affectedClassName: '',
      action: '',
      screenName: '',
      moduleName: '',
      tableName: '',
      recordName: '',
      recordId: ''
    };
  }

  // Normalizes Date, Moment-like, or parseable values into a Date or null.
  private coerceToDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const maybeMoment = value as { toDate?: () => Date; valueOf?: () => number };
    if (typeof maybeMoment.toDate === 'function') {
      const fromMoment = maybeMoment.toDate();
      return fromMoment instanceof Date && !Number.isNaN(fromMoment.getTime()) ? fromMoment : null;
    }
    if (typeof maybeMoment.valueOf === 'function') {
      const fromValue = new Date(maybeMoment.valueOf());
      return Number.isNaN(fromValue.getTime()) ? null : fromValue;
    }
    const parsed = new Date(value as string | number);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  // Validates that To Date is not earlier than From Date (date-only compare).
  private isToDateBeforeFromDate(): boolean {
    const fromDate = this.coerceToDate(this.filters.fromDate);
    const toDate = this.coerceToDate(this.filters.toDate);
    if (!fromDate || !toDate) {
      return false;
    }
    // Compare calendar dates only (ignore time-of-day leftovers).
    const from = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const to = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
    return to.getTime() < from.getTime();
  }

  // private restoreSavedFilter(): void {
  //   const raw = localStorage.getItem(this.filterStorageKey);
  //   if (!raw) {
  //     return;
  //   }
  //   try {
  //     const saved = JSON.parse(raw);
  //     const todayDefaults = this.createDefaultFilters();
  //     this.filters = {
  //       fromDate: todayDefaults.fromDate,
  //       toDate: todayDefaults.toDate,
  //       user: saved.user ?? '',
  //       affectedClassName: saved.affectedClassName ?? '',
  //       action: saved.action ? String(saved.action).trim().toUpperCase() : '',
  //       screenName: saved.screenName ?? '',
  //       moduleName: saved.moduleName ?? '',
  //       tableName: saved.tableName ?? '',
  //       recordName: saved.recordName ?? '',
  //       recordId: saved.recordId ?? ''
  //     };
  //   } catch {
  //     localStorage.removeItem(this.filterStorageKey);
  //   }
  // }

  // private restoreSelectedLanguage(): void {
  //   const saved = localStorage.getItem(this.languageStorageKey);
  //   if (!saved) {
  //     return;
  //   }
  //   const isSupported = this.languageOptions.some((option) => option.code === saved);
  //   if (isSupported) {
  //     this.selectedLanguage = saved;
  //   }
  // Resolves the selected language code to its display label for exports.
  private getSelectedLanguageLabel(): string {
    return (
      this.languageOptions.find((option) => option.code === this.selectedLanguage)?.label ||
      this.selectedLanguage
    );
  }

  // Returns escaped HTML for action badges rendered inside AG Grid cells.
  private renderBadge(value: string, cssClass: string): string {
    return `<span class="${cssClass}">${this.escapeHtml(value || '-')}</span>`;
  }

  // Escapes HTML special characters for safe PDF/table HTML injection.
  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}